import { useContext, useEffect, useState, useRef } from 'react'
import { GlobalUIContext } from '../../contexts/GlobalUIContext'
import { AuthContext } from '../../contexts/AuthContext'
import MainLayout from '@components/MainLayout/MainLayout'
import DetailLayout from '@components/Details/DetailLayout'
import ItemCard from '@components/Cards/BidItem'
import InfoSlideOut from '@components/InfoSlideOut/InfoSlideOut'
import BidModal from '@components/BidModal/BidModal'
import BanModal from '@components/BanModal/BanModal'
import OfferModal from '@components/OfferModal/OfferModal'
import Filters from '@components/AuctionDetails/Filters'
import PaginationLayout from '@components/PaginationLayout'
import useFilter from '@components/AuctionDetails/useFilter'
import useCookieState from '../../utilities/hooks/useCookieState'
import SkeletonCards from '@components/SkeletonCards'
import { useRouter } from 'next/router'
import classNames from 'classnames'

const AuctionDetails = (props: any) => {
  const {
    auction,
    allOffers,
    initialItems,
    initialPagination,
    conditions,
    initListView = false,
  } = props
  const { setMainOverflowY } = useContext<any>(GlobalUIContext)
  const [infoSlideOutOpen, setInfoSlideOutOpen] = useState<any>(false)
  const [isListView, setIsListView] = useCookieState('isListView', initListView)
  const [showBidModal, setShowBidModal] = useState<any>(false)
  const [showBanModal, setShowBanModal] = useState<any>(false)
  const [showBinMaoModal, setShowBinMaoModal] = useState<any>(false)
  const [negativeZBid, setNegativeZBid] = useState<any>(true)
  const [negativeZBan, setNegativeZBan] = useState<any>(true)
  const [negativeZBinMao, setNegativeZBinMao] = useState<any>(true)
  const [modalType, setModalType] = useState<any>()
  const [modalAction, setModalAction] = useState<any>(null)
  const [modalData, setModalData] = useState<any>({})
  const [modalItem, setModalItem] = useState<any>({})
  const [offers, setOffers] = useState<any>(allOffers)
  const [bins, setBins] = useState<any>([])
  const itemsRef = useRef<any>({})

  const [auctionClosed, setAuctionClosed] = useState<boolean>(
    auction.pastAuction || auction.itemCount <= 0
  )
  const { items, filter, pagination, isLoading, handlers } = useFilter({
    initialPagination,
    initialItems,
    isRefreshDisabled: auction.pastAuction,
  })
  const router = useRouter()
  const { instance } = useContext<any>(AuthContext)

  useEffect(() => {
    setMainOverflowY(!infoSlideOutOpen)
  }, [infoSlideOutOpen])

  const hideModal = (type: any) => {
    let timeoutFunc: any = null
    if (type === 'Bid') {
      setShowBidModal(false)
      timeoutFunc = () => setNegativeZBid(true)
    }
    if (type === 'Ban') {
      setShowBanModal(false)
      timeoutFunc = () => setNegativeZBan(true)
    }
    if (type === 'BinMao') {
      setShowBinMaoModal(false)
      timeoutFunc = () => setNegativeZBinMao(true)
    }
    setTimeout(timeoutFunc, 200)
  }

  const toggleModal = (show: any, type: any) => {
    if (show) {
      if (type === 'Bid') {
        setShowBidModal(true)
        setNegativeZBid(false)
      }
      if (type === 'Ban') {
        setShowBanModal(true)
        setNegativeZBan(false)
      }
      if (type === 'BinMao') {
        setShowBinMaoModal(true)
        setNegativeZBinMao(false)
      }
    } else {
      hideModal(type)
    }
  }

  useEffect(() => {
    const fetchAuctionDetails = async () => {
      if (auctionClosed) return
      const url = `/auction/isClosed/${auction.id}`
      const { data }: { data: any } = await instance.get(url)
      if (data) {
        setAuctionClosed(true)
      }
    }

    //fetch every 30 seconds
    const interval = setInterval(fetchAuctionDetails, 60000)

    //clear the interval when the component unmounts
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const goToItemId =
      router.query.itemId || items.find((item) => !item.itemClosed)?.id
    if (!goToItemId) return

    if (itemsRef.current && itemsRef.current[goToItemId]) {
      !itemsRef.current[goToItemId].scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      })
    }
    return () => {
      itemsRef.current = {}
    }
  }, [items])

  return (
    <div>
      <MainLayout
        title="Auction Detail"
        previewImage={auction.picUrl[0]}
        previewDescription={auction.title}
        hideOverflow={false}
      >
        <DetailLayout
          auction={auction}
          infoSlideOutOpen={infoSlideOutOpen}
          setInfoSlideOutOpen={setInfoSlideOutOpen}
          itemClosed={auctionClosed}
          showBackButton={false}
        >
          <div className="w-full">
            <Filters
              auction={auction}
              conditions={conditions}
              filter={filter}
              handlers={handlers}
              isListView={isListView}
              setIsListView={setIsListView}
            />
            <div
              className={classNames(
                'pt-4 pb-11 xl:pb-16 min-h-112 lg:min-h-none',
                `pointer-events-${isLoading ? 'none' : 'auto'}`
              )}
            >
              <PaginationLayout
                {...pagination}
                pageChange={handlers.pageChange}
              >
                <div
                  className={`grid grid-cols-1 gap-5 md:gap-6 pb-8 xl:pb-16 ${
                    isListView
                      ? 'xl:grid-cols-1'
                      : 'md:grid-cols-3 2xl:grid-cols-4'
                  }`}
                >
                  {isLoading ? (
                    <SkeletonCards numberOfCards={24} />
                  ) : items.length ? (
                    items.map((item: any, index: any) => (
                      <div
                        key={item.id}
                        className={item.id ? 'block' : 'flex'}
                        ref={(el) => (itemsRef.current[item.id] = el)}
                      >
                        {item.id && (
                          <ItemCard
                            item={item}
                            bins={bins}
                            offers={offers}
                            options={{
                              isListView,
                              cardIndex: index,
                            }}
                            handlers={{
                              setModalAction,
                              setModalData,
                              setModalItem,
                              setModalType,
                              toggleModal,
                            }}
                            location={auction?.location}
                          />
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center col-span-3">
                      Sorry no items available.{' '}
                      <span
                        className="font-bold underline"
                        role="button"
                        tabIndex={0}
                        onClick={handlers.clearAllFilters}
                        onKeyDown={(e: any) => {
                          if (e.key === 'Enter') {
                            handlers.clearAllFilters()
                          }
                        }}
                      >
                        View All Items
                      </span>
                    </p>
                  )}
                </div>
              </PaginationLayout>
            </div>
          </div>
        </DetailLayout>

        <InfoSlideOut
          open={infoSlideOutOpen}
          setOpen={setInfoSlideOutOpen}
          auction={auction}
        />
      </MainLayout>

      {showBidModal && (
        <BidModal
          showModal={showBidModal}
          negativeZ={negativeZBid}
          toggleModal={toggleModal}
          modalAction={modalAction}
          modalData={modalData}
        />
      )}
      {showBanModal && (
        <BanModal
          showModal={showBanModal}
          negativeZ={negativeZBan}
          toggleModal={toggleModal}
          modalData={modalData}
        />
      )}
      {showBinMaoModal && (
        <OfferModal
          showModal={showBinMaoModal}
          negativeZ={negativeZBinMao}
          auction={auction}
          item={modalItem}
          type={modalType}
          bins={bins}
          setBins={setBins}
          offers={offers}
          setOffers={setOffers}
          toggleModal={toggleModal}
        />
      )}
    </div>
  )
}

export default AuctionDetails
